import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Phone, ShieldCheck, Truck, Wrench, PackageCheck, Mail } from "lucide-react";
import Seo from "@/components/Seo";
import { PrimaryLink, SectionHeading, Eyebrow, GoldRule } from "@/components/ui-bits";
import { PRODUCTS, COMPANY } from "@/data/site";
import { ReadyKitsTeaser } from "@/components/system-builder/ReadyKitsTeaser";
import handshake from "@/assets/brand/team-handshake.png";
import recognition from "@/assets/brand/dyness-global-recognition-2026.webp";
import salesSupport from "@/assets/brand/sales-support.png";
import craigPhoto from "@/assets/brand/team/craig-jobson.jpg";
import { ReviewsShowcase } from "@/components/Reviews";
import TradeSignup from "@/components/TradeSignup";
import { DownloadCards } from "@/components/DownloadCards";
import VideoGallery from "@/components/VideoGallery";
import { WelcomeVideo } from "@/components/WelcomeVideo";
import { LaunchRibbon } from "@/components/LaunchRibbon";
import { PhotoCarousel } from "@/components/PhotoCarousel";
import siteClients from "@/assets/brand/team/about-6-site-clients.png";
import siteTechQuestions from "@/assets/brand/team/about-7-site-tech-questions.png";
import siteCommercial from "@/assets/brand/team/about-8-site-commercial.png";
import siteDynessWalkthrough from "@/assets/brand/team/about-9-site-dyness-walkthrough.png";
import siteCrewDebrief from "@/assets/brand/team/about-10-site-crew-debrief.png";

function CecBadge() {
  return (
    <div
      className="absolute top-3 right-3 z-10 w-14 h-14 sm:w-16 sm:h-16 rounded-full bg-[#41A62A] text-white flex flex-col items-center justify-center shadow-lg ring-2 ring-white/80"
      data-testid="cec-badge"
      aria-label="CEC Listed"
    >
      <span className="font-display font-bold leading-none text-sm sm:text-base">CEC</span>
      <span className="text-[9px] sm:text-[10px] leading-none mt-0.5 tracking-wide">Listed</span>
    </div>
  );
}

export default function Home() {
  return (
    <>
      <Seo
        title="EI Battery Solutions — Wholesale battery supply for the Australian trade"
        description="Batteries, hybrid inverters and solar panels, with trade quotations, Australia-wide delivery and product support for your installation team."
      />

      {/* Hero */}
      <section className="bg-ei-navy text-ei-white overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-14 lg:py-16 grid lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-6 ei-rise">
            <Eyebrow className="text-ei-gold">Wholesale · Trade only · Australia-wide</Eyebrow>
            <h1 className="mt-5 text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.05]">
              Wholesale battery supply for the Australian trade
            </h1>
            <p className="mt-6 text-lg text-ei-white/75 leading-relaxed max-w-xl">
              A family-owned company with over 26 years of energy and electrical experience, supplying batteries,
              hybrid inverters and solar panels to registered electrical contractors, builders and property
              developers Australia-wide — with trade quotations, delivery and product support for your
              installation team.
            </p>
            <div className="mt-9 flex flex-wrap gap-4">
              <PrimaryLink to="/request-a-quote" testId="hero-quote-cta" className="!bg-ei-gold !text-ei-navy hover:!bg-[#b89747]">
                Request a wholesale quote <ArrowRight className="w-4 h-4 ml-2" />
              </PrimaryLink>
              <Link
                to="/products"
                data-testid="hero-shop-cta"
                className="font-display text-sm font-bold text-ei-white border border-ei-white/30 px-6 py-3.5 rounded-sm hover:bg-ei-white/10 transition-colors inline-flex items-center justify-center min-h-[44px]"
              >
                Shop now <ArrowRight className="w-4 h-4 ml-2" />
              </Link>
            </div>
          </div>

          <div className="lg:col-span-6 ei-rise" style={{ animationDelay: "0.1s" }}>
            <img
              src={recognition}
              alt="Dyness global recognition — EUPD Research Top Brand PV Storage awards across Brazil, Czech Republic, South Africa, Romania, Poland, Germany, Mexico, LATAM, Africa, Kenya and the United Kingdom, 2022 to 2026"
              className="mx-auto mb-6 h-auto max-h-[130px] w-full max-w-2xl object-contain sm:max-h-[180px] lg:max-w-none"
              loading="eager"
              data-testid="hero-recognition-emblem"
            />
            <div className="rounded-md overflow-hidden border border-ei-white/10 shadow-2xl">
              <img
                src={handshake}
                alt="EI Battery Solutions team meeting with electrical contractors"
                className="w-full h-full object-cover"
                loading="eager"
              />
            </div>
            <p className="mt-4 text-center text-sm font-semibold text-ei-gold tracking-wide" data-testid="hero-image-caption">
              Family company with over 26 years experience
            </p>
          </div>
        </div>
      </section>

      {/* Products overview */}
      <section className="bg-white border-y border-ei-mist">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 sm:pt-10 lg:pt-12 pb-12 sm:pb-16 lg:pb-24">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6">
            <SectionHeading eyebrow="Launch range" title="EI Battery Launch Range" intro="Systems starting from $3,580." />
            <Link to="/products" className="font-display text-sm font-bold text-ei-navy hover:text-ei-golddark inline-flex items-center gap-2" data-testid="home-view-products">
              View all products <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
            {PRODUCTS.filter((p) => ["cygni-all-in-one", "power-stack-pro-100", "power-box-g2"].includes(p.slug)).map((p) => (
              <Link
                key={p.slug}
                to={`/products/${p.slug}`}
                data-testid={`home-product-${p.slug}`}
                className="group flex flex-col rounded-md overflow-hidden border border-ei-mist hover:border-ei-navy/40 hover:shadow-xl transition-all bg-white"
              >
                <div className="relative bg-white overflow-hidden">
                  {p.originalPrice && <LaunchRibbon testId={`launch-ribbon-home-${p.slug}`} />}
                  <img src={p.homeImage || p.image} alt={p.imageAlt || p.name} loading="lazy" className="w-full h-auto object-contain group-hover:scale-[1.02] transition-transform duration-500" />
                </div>
                <div className="p-5 flex items-center justify-between gap-3 border-t border-ei-mist">
                  <div>
                    {p.originalPrice && (
                      <span className="font-display font-bold tnum text-ei-graphite/45 text-sm line-through mr-2" data-testid={`home-product-${p.slug}-was`}>{p.originalPrice}</span>
                    )}
                    <span className="font-display font-bold tnum text-ei-navy text-xl">{p.priceLabel}</span>
                    <span className="block text-xs text-ei-graphite/55">+ GST, wholesale</span>
                  </div>
                  <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-ei-navy whitespace-nowrap">View <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" /></span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Out on site */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-10 sm:pt-14" data-testid="home-site-carousel-section">
        <SectionHeading
          eyebrow="Out on site"
          title="Real jobs, real trade customers"
          intro="A look at the work our trade customers are putting in the ground around the country."
        />
        <div className="mt-8">
          <PhotoCarousel
            testId="home-site-carousel"
            slides={[
              { src: siteClients, caption: "Handover day — a family's new Dyness battery switched on and running" },
              { src: siteTechQuestions, caption: "Walking the sparky through the inverter and battery pairing on site" },
              { src: siteCommercial, caption: "Checking the stack and the paperwork on a commercial install" },
              { src: siteDynessWalkthrough, caption: "Talking the crew through a twin Dyness and Solis switchboard setup" },
              { src: siteCrewDebrief, caption: "Job done — wrapping up with the crew after commissioning" },
            ]}
          />
        </div>
      </section>

      {/* Supply-only value props */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16 lg:py-24">
        <SectionHeading
          eyebrow="Built for the trade"
          title="A wholesale partner that stays in its lane"
          intro="We supply the equipment and back your team. You keep the customer relationship, the design and the installation."
        />
        <div className="mt-12 grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6" data-testid="trust-cards">
            {[
              { icon: PackageCheck, title: "Trade-only supply", body: "Sold to registered electrical contractors, builders and property developers approved for a trade account." },
              { icon: Wrench, title: "You own the install", body: "Contractors design, install and commission. We never compete for your customer." },
              { icon: Truck, title: "Australia-wide service", body: "Collect from us or arrange delivery to your site. Typical estimate around seven days." },
              { icon: ShieldCheck, title: "Product & warranty support", body: "Dyness provides the manufacturer warranty; we help you lodge, document and follow up every claim." },
            ].map((f) => (
              <div key={f.title} className="bg-white border border-ei-mist rounded-md p-6 sm:p-8 hover:border-ei-navy/25 transition-colors">
                <f.icon className="w-8 h-8 text-ei-golddark" strokeWidth={1.5} />
                <h3 className="mt-5 text-lg font-semibold text-ei-navy">{f.title}</h3>
                <p className="mt-2 text-sm text-ei-graphite/85 leading-relaxed">{f.body}</p>
              </div>
            ))}
          </div>
          <div className="self-center bg-white border border-ei-mist rounded-md p-6 sm:p-8 flex items-center justify-center" data-testid="video-showcase-box">
            <VideoGallery embedded />
          </div>
        </div>
      </section>

      <ReadyKitsTeaser />

      {/* Process — navy feature band for visual contrast */}
      <section className="bg-ei-navy text-ei-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 sm:py-20 lg:py-24">
          <div className="text-center mx-auto max-w-2xl">
            <Eyebrow className="text-ei-gold flex items-center justify-center gap-3">How it works</Eyebrow>
            <h2 className="mt-4 text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight">From enquiry to delivery</h2>
          </div>
          <WelcomeVideo />
          <p className="mt-4 text-center text-sm text-ei-white/70" data-testid="welcome-video-caption">
            Featuring our launch range — the <strong className="text-ei-white">Cygni All in One</strong>, <strong className="text-ei-white">Stack100 Pro</strong> and <strong className="text-ei-white">PowerBox G2</strong>.
          </p>

          {/* Trade reviews — directly under the welcome video */}
          <div className="mt-14">
            <div className="text-center mx-auto max-w-2xl">
              <Eyebrow className="text-ei-gold flex items-center justify-center gap-3">Trade feedback</Eyebrow>
              <h3 className="mt-3 text-xl sm:text-2xl font-bold tracking-tight">What contractors say</h3>
            </div>
            <div className="mt-8">
              <ReviewsShowcase dark />
            </div>
          </div>

          <div className="mt-8 border-t border-ei-white/15 pt-6 text-sm text-ei-white/80" data-testid="home-trading-terms">
            <p><span className="font-semibold text-ei-white">Trading Terms:</span> Full payment before pick up.</p>
            <p className="mt-1 text-xs text-ei-white/55">*Pricing Ex Factory. Delivery available at additional cost.</p>
          </div>
        </div>
      </section>

      {/* Australia-wide delivery band */}
      <section className="relative" data-testid="home-delivery-band">
        <div className="relative h-64 sm:h-80 lg:h-96 overflow-hidden">
          <img
            src="https://images.unsplash.com/photo-1601584115197-04ecc0da31d7?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600&ixlib=rb-4.1.0"
            alt="Delivery truck on an Australian highway"
            className="w-full h-full object-cover"
            loading="lazy"
          />
          <div className="absolute inset-0 bg-ei-navy/65" />
          <div className="absolute inset-0 flex items-center">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
              <div className="max-w-xl text-ei-white">
                <div className="ei-eyebrow text-ei-gold">Australia-wide service</div>
                <h2 className="mt-3 text-3xl sm:text-4xl font-bold tracking-tight">Delivered across Australia</h2>
                <p className="mt-3 text-ei-white/85 leading-relaxed">Collect from us or arrange delivery to your site. Delivery times can vary depending on location.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Ordering in four steps */}
      <section className="bg-ei-white border-b border-ei-mist" data-testid="home-order-steps">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
          <div className="lg:flex lg:items-end lg:justify-between lg:gap-10">
            <div className="max-w-xl">
              <div className="ei-eyebrow text-ei-gold">Ordering is simple</div>
              <h2 className="mt-3 text-3xl sm:text-4xl font-bold tracking-tight text-ei-navy">Four steps from order to site</h2>
              <p className="mt-3 text-ei-graphite/85 leading-relaxed">Trade pricing, card payment and Australia-wide dispatch from the warehouse closest to your job.</p>
            </div>
            <Link
              to="/delivery-and-support#order-flow"
              className="mt-6 lg:mt-0 inline-flex items-center gap-2 text-sm font-bold text-ei-navy underline underline-offset-4 hover:text-ei-golddark transition-colors"
              data-testid="home-order-steps-link"
            >
              See the full nine-step order flow
            </Link>
          </div>
          <ol className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              { n: "01", t: "Order online", d: "Build your cart at trade prices and check out from your account." },
              { n: "02", t: "Pay by card", d: "Stripe card payment with freight added, and you get an EI order number." },
              { n: "03", t: "We pick and pack", d: "The warehouse closest to your site picks, packs and dispatches." },
              { n: "04", t: "Delivered to you", d: "Our carrier delivers, and you get confirmation and tracking." },
            ].map((step) => (
              <li key={step.n} className="bg-white border border-ei-mist rounded-md p-6 hover:border-ei-gold transition-colors" data-testid={`home-order-step-${step.n}`}>
                <div className="font-display text-2xl font-bold text-ei-gold/70 tnum">{step.n}</div>
                <h3 className="mt-2 text-base font-semibold text-ei-navy">{step.t}</h3>
                <p className="mt-2 text-sm text-ei-graphite/85 leading-relaxed">{step.d}</p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* Meet the family */}
      <section className="bg-white border-y border-ei-mist">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16 lg:py-24">
          <SectionHeading
            eyebrow="Meet the family"
            title="The people behind EI Battery Solutions"
            intro="A family-run wholesale partner backing electrical contractors across Australia."
          />
          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-8">
            {[
              {
                name: "Craig Jobson",
                role: "Chief Executive Officer",
                email: "craig@eibattery.com.au",
                img: craigPhoto,
                bio: "Craig brings over 26 years of experience in the energy industry to EI Battery Solutions. He leads the business with a focus on supporting electrical contractors across Australia.",
              },
              {
                name: "Charles Gardiner",
                role: "Head of Sales",
                email: "sales@eibattery.com.au",
                img: "/media/owner-supplied/charles-gardiner.png",
                bio: "Charles leads sales enquiries, helping electrical contractors with product information and quotations.",
              },
            ].map((m) => (
              <div key={m.name} className="flex flex-col sm:flex-row border border-ei-mist rounded-md overflow-hidden bg-ei-white" data-testid={`team-${m.name.split(" ")[0].toLowerCase()}`}>
                <div className="w-full sm:w-44 h-60 shrink-0 bg-ei-mist/40 overflow-hidden flex items-center justify-center" data-testid={`team-photo-frame-${m.name.split(' ')[0].toLowerCase()}`}>
                  {m.img ? (
                    <img src={m.img} alt={`${m.name}, ${m.role} at EI Battery Solutions`} className="w-full h-full object-cover object-top" loading="lazy" data-testid={`team-photo-${m.name.split(' ')[0].toLowerCase()}`} />
                  ) : (
                    <span className="text-xs text-ei-graphite/50 text-center px-3">Photo coming soon</span>
                  )}
                </div>
                <div className="p-6 sm:p-7 flex flex-col">
                  <h3 className="text-xl font-semibold text-ei-navy">{m.name}</h3>
                  <div className="mt-1 ei-eyebrow">{m.role}</div>
                  <p className="mt-3 text-sm text-ei-graphite/85 leading-relaxed flex-1">{m.bio}</p>
                  <a href={`mailto:${m.email}`} className="mt-4 inline-flex items-center gap-2 text-sm font-semibold text-ei-navy hover:text-ei-golddark break-all" data-testid={`team-email-${m.name.split(" ")[0].toLowerCase()}`}>
                    <Mail className="w-4 h-4 shrink-0" /> {m.email}
                  </a>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Trade account signup */}
      <TradeSignup />

      {/* Contact band */}
      <section className="bg-ei-navy text-ei-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 lg:py-20 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            <GoldRule />
            <h2 className="mt-5 text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight">
              Ready to price your next job?
            </h2>
            <p className="mt-4 text-ei-white/75 max-w-xl leading-relaxed">
              Send your trade details and quantities. Our sales team, led by {COMPANY.headOfSales}, prepares your
              wholesale quote and follows up by phone.
            </p>
            <div className="mt-8 flex flex-wrap gap-4">
              <PrimaryLink to="/request-a-quote" testId="home-bottom-quote" className="!bg-ei-gold !text-ei-navy hover:!bg-[#b89747]">
                Request a quote
              </PrimaryLink>
              <a href={COMPANY.phoneHref} className="font-display text-sm font-bold text-ei-white border border-ei-white/30 px-6 py-3.5 rounded-sm hover:bg-ei-white/10 inline-flex items-center min-h-[44px]">
                <Phone className="w-4 h-4 mr-2" /> {COMPANY.phone}
              </a>
            </div>
          </div>
          <div className="lg:col-span-5">
            <div className="rounded-md overflow-hidden border border-ei-white/10">
              <img src={salesSupport} alt="EI Battery Solutions sales and technical support desk" className="w-full object-cover" loading="lazy" />
            </div>
          </div>
        </div>
      </section>
      {/* Document downloads */}
      <section className="bg-ei-white border-t border-ei-mist" data-testid="home-catalogue">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
          <div className="max-w-2xl">
            <div className="ei-eyebrow text-ei-gold">Trade documents</div>
            <h2 className="mt-3 text-3xl sm:text-4xl font-bold tracking-tight text-ei-navy">EI Battery collection — October 2026</h2>
            <p className="mt-4 text-ei-graphite/85 leading-relaxed">
              Kits and individual products with SKUs and AUD pricing, ex and inc GST, plus how freight is charged.
              Equipment supply only — installation and commissioning are quoted separately.
            </p>
          </div>
          <div className="mt-10">
            <DownloadCards />
          </div>
        </div>
      </section>
    </>
  );
}
